import {
  CELL_SIZE,
  COLORS,
  GHOST_ALPHA,
  BOARD_WIDTH,
  BOARD_HEIGHT,
} from "./constants";
import { GameState, Piece, PieceType } from "./state";
import { getCells, getShape, spawnPiece } from "./piece";
import { getGhostY } from "./board";
import { NetworkState } from "./network";
import { GameStateMsg, PlayerNetState } from "./protocol";

let mainCtx: CanvasRenderingContext2D;
let nextCtx: CanvasRenderingContext2D;
let holdCtx: CanvasRenderingContext2D;
let opponentCtx: CanvasRenderingContext2D | null = null;

// Cached DOM elements for stats display
let scoreEl: HTMLElement | null = null;
let linesEl: HTMLElement | null = null;
let levelEl: HTMLElement | null = null;

function setTextContent(el: HTMLElement | null, text: string): void {
  if (el) el.textContent = text;
}

export function initRenderer(): void {
  const mainCanvas = document.getElementById(
    "main-canvas",
  ) as HTMLCanvasElement;
  const nextCanvas = document.getElementById(
    "next-canvas",
  ) as HTMLCanvasElement;
  const holdCanvas = document.getElementById(
    "hold-canvas",
  ) as HTMLCanvasElement;
  const opponentCanvas = document.getElementById(
    "opponent-canvas",
  ) as HTMLCanvasElement | null;

  mainCtx = mainCanvas.getContext("2d")!;
  nextCtx = nextCanvas.getContext("2d")!;
  holdCtx = holdCanvas.getContext("2d")!;
  opponentCtx = opponentCanvas ? opponentCanvas.getContext("2d")! : null;

  // Cache stats DOM elements
  scoreEl = document.getElementById("score");
  linesEl = document.getElementById("lines");
  levelEl = document.getElementById("level");
}

export function setMultiplayerLayout(enabled: boolean): void {
  const opponentArea = document.getElementById(
    "player2-area",
  ) as HTMLElement | null;
  if (opponentArea) {
    opponentArea.style.display = enabled ? "flex" : "none";
  }
}

export function setPlayerLabels(myPlayerId: number): void {
  const myLabel = document.getElementById("player1-label");
  const oppLabel = document.getElementById("player2-label");
  if (!myLabel || !oppLabel) return;

  if (myPlayerId === 0) {
    myLabel.textContent = "Player 1 (You)";
    oppLabel.textContent = "Player 2";
  } else if (myPlayerId === 1) {
    myLabel.textContent = "Player 2 (You)";
    oppLabel.textContent = "Player 1";
  } else {
    myLabel.textContent = "Player 1";
    oppLabel.textContent = "Player 2";
  }
}

export function render(state: GameState): void {
  renderBoard(state);
  renderNext(state.nextPiece);
  renderHold(state.heldPiece);
  renderStats(state);
}

export function renderMenu(): void {
  const ctx = mainCtx;
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

  ctx.fillStyle = "#fff";
  ctx.font = "bold 28px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const centerX = (BOARD_WIDTH * CELL_SIZE) / 2;
  const centerY = (BOARD_HEIGHT * CELL_SIZE) / 2;

  ctx.fillText("BATTLETRIS", centerX, centerY - 100);

  ctx.font = "18px monospace";
  ctx.fillText("1 - Single Player", centerX, centerY - 20);
  ctx.fillText("2 - Multiplayer", centerX, centerY + 20);
  ctx.fillText("H - Help", centerX, centerY + 50);

  ctx.font = "12px monospace";
  ctx.fillStyle = "#888";
  ctx.fillText("Controls:", centerX, centerY + 80);
  ctx.fillText("Arrows: Move/Drop", centerX, centerY + 100);
  ctx.fillText("Up/X: Rotate CW", centerX, centerY + 120);
  ctx.fillText("Z: Rotate CCW", centerX, centerY + 140);
  ctx.fillText("C/Shift: Hold", centerX, centerY + 160);
  ctx.fillText("Space: Hard Drop", centerX, centerY + 180);

  // Clear side panels
  nextCtx.fillStyle = "#1a1a1a";
  nextCtx.fillRect(0, 0, 80, 80);
  holdCtx.fillStyle = "#1a1a1a";
  holdCtx.fillRect(0, 0, 80, 80);

  setTextContent(scoreEl, "0");
  setTextContent(linesEl, "0");
  setTextContent(levelEl, "1");
}

export function renderMultiplayer(
  networkState: NetworkState,
  gameState: GameStateMsg | null,
  myPlayerId: number,
  countdownTicks: number,
  winnerId: number,
  playerCount: number = 0,
  myReady: boolean = false,
  opponentReady: boolean = false,
): void {
  const ctx = mainCtx;

  if (networkState === "disconnected" || networkState === "connecting") {
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

    ctx.fillStyle = "#fff";
    ctx.font = "20px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      networkState === "connecting" ? "Connecting..." : "Disconnected",
      (BOARD_WIDTH * CELL_SIZE) / 2,
      (BOARD_HEIGHT * CELL_SIZE) / 2,
    );
    ctx.font = "14px monospace";
    ctx.fillText(
      "Press ESC to go back",
      (BOARD_WIDTH * CELL_SIZE) / 2,
      (BOARD_HEIGHT * CELL_SIZE) / 2 + 40,
    );
    return;
  }

  if (networkState === "connected" || networkState === "matched") {
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

    ctx.fillStyle = "#fff";
    ctx.font = "20px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (networkState === "connected") {
      ctx.fillText(
        "Waiting for opponent...",
        (BOARD_WIDTH * CELL_SIZE) / 2,
        (BOARD_HEIGHT * CELL_SIZE) / 2,
      );
      ctx.font = "14px monospace";
      ctx.fillStyle = "#888";
      ctx.fillText(
        `Players online: ${playerCount}`,
        (BOARD_WIDTH * CELL_SIZE) / 2,
        (BOARD_HEIGHT * CELL_SIZE) / 2 + 40,
      );
    } else {
      ctx.fillText(
        "Opponent found!",
        (BOARD_WIDTH * CELL_SIZE) / 2,
        (BOARD_HEIGHT * CELL_SIZE) / 2 - 40,
      );
      ctx.font = "16px monospace";
      if (!myReady) {
        ctx.fillText(
          "Press SPACE when ready",
          (BOARD_WIDTH * CELL_SIZE) / 2,
          (BOARD_HEIGHT * CELL_SIZE) / 2,
        );
      } else {
        ctx.fillStyle = "#4a4";
        ctx.fillText(
          "Ready!",
          (BOARD_WIDTH * CELL_SIZE) / 2,
          (BOARD_HEIGHT * CELL_SIZE) / 2,
        );
      }
      ctx.font = "14px monospace";
      ctx.fillStyle = "#888";
      if (opponentReady) {
        ctx.fillStyle = "#4a4";
        ctx.fillText(
          "Opponent is ready",
          (BOARD_WIDTH * CELL_SIZE) / 2,
          (BOARD_HEIGHT * CELL_SIZE) / 2 + 30,
        );
      } else {
        ctx.fillText(
          "Waiting for opponent...",
          (BOARD_WIDTH * CELL_SIZE) / 2,
          (BOARD_HEIGHT * CELL_SIZE) / 2 + 30,
        );
      }
    }
    return;
  }

  if (networkState === "countdown") {
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

    ctx.fillStyle = "#fff";
    ctx.font = "bold 48px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const seconds = Math.ceil(countdownTicks / 60);
    ctx.fillText(
      seconds.toString(),
      (BOARD_WIDTH * CELL_SIZE) / 2,
      (BOARD_HEIGHT * CELL_SIZE) / 2,
    );
    return;
  }

  // Playing or finished - render game state
  if (!gameState) return;
  if (myPlayerId < 0 || myPlayerId > 1) return;

  const myState = gameState.players[myPlayerId];
  const oppState = gameState.players[1 - myPlayerId];

  // Render my board on main canvas
  renderNetBoard(
    ctx,
    myState,
    BOARD_WIDTH * CELL_SIZE,
    BOARD_HEIGHT * CELL_SIZE,
    myState.flipScreenTicks > 0,
  );
  renderEffectBadges(ctx, myState);
  if (opponentCtx) {
    renderNetBoard(
      opponentCtx,
      oppState,
      BOARD_WIDTH * CELL_SIZE,
      BOARD_HEIGHT * CELL_SIZE,
      false,
    );
    renderEffectBadges(opponentCtx, oppState);
  }

  // Update stats
  setTextContent(scoreEl, myState.score.toString());
  setTextContent(linesEl, myState.lines.toString());
  setTextContent(levelEl, myState.level.toString());

  // Update inventory
  renderInventory(myState.inventory, myState.inventoryCount);

  // Render next and hold pieces
  if (myState.nextPiece > 0) {
    renderNext(myState.nextPiece as PieceType);
  }
  if (myState.heldPiece > 0) {
    renderHold(myState.heldPiece as PieceType);
  } else {
    holdCtx.fillStyle = "#1a1a1a";
    holdCtx.fillRect(0, 0, 80, 80);
  }

  // Game over overlay
  if (networkState === "finished") {
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

    ctx.fillStyle = "#fff";
    ctx.font = "bold 24px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const centerX = (BOARD_WIDTH * CELL_SIZE) / 2;
    const centerY = (BOARD_HEIGHT * CELL_SIZE) / 2;

    const isWinner = winnerId === myPlayerId;
    ctx.fillText(
      isWinner ? "YOU WIN!" : "YOU LOSE",
      centerX,
      centerY - 50,
    );

    ctx.font = "16px monospace";
    if (!myReady) {
      ctx.fillText("SPACE - Play Again", centerX, centerY);
    } else {
      ctx.fillStyle = "#4a4";
      ctx.fillText("Waiting for opponent...", centerX, centerY);
    }

    ctx.font = "14px monospace";
    if (opponentReady && !myReady) {
      ctx.fillStyle = "#4a4";
      ctx.fillText("Opponent wants rematch!", centerX, centerY + 30);
    } else if (!opponentReady && myReady) {
      ctx.fillStyle = "#888";
      ctx.fillText("Opponent deciding...", centerX, centerY + 30);
    }

    ctx.fillStyle = "#888";
    ctx.fillText("ESC - Leave", centerX, centerY + 60);
  }
}

function renderNetBoard(
  ctx: CanvasRenderingContext2D,
  state: PlayerNetState,
  width: number,
  height: number,
  flip: boolean,
): void {
  ctx.save();
  if (flip) {
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
  }

  // Clear
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, width, height);

  // Draw grid lines
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 1;
  for (let x = 0; x <= BOARD_WIDTH; x++) {
    ctx.beginPath();
    ctx.moveTo(x * CELL_SIZE, 0);
    ctx.lineTo(x * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);
    ctx.stroke();
  }
  for (let y = 0; y <= BOARD_HEIGHT; y++) {
    ctx.beginPath();
    ctx.moveTo(0, y * CELL_SIZE);
    ctx.lineTo(BOARD_WIDTH * CELL_SIZE, y * CELL_SIZE);
    ctx.stroke();
  }

  // Draw locked cells
  for (let y = 0; y < BOARD_HEIGHT; y++) {
    for (let x = 0; x < BOARD_WIDTH; x++) {
      const cell = state.board[y][x];
      if (cell !== 0) {
        const color = COLORS[cell] || "#808080";
        drawCell(ctx, x, y, color, 1);
      }
    }
  }

  // Draw current piece
  if (state.pieceType > 0) {
    const shape = getShape(state.pieceType as PieceType, state.pieceRotation);
    const color = COLORS[state.pieceType] || "#fff";

    for (let ly = 0; ly < shape.length; ly++) {
      for (let lx = 0; lx < shape[ly].length; lx++) {
        if (shape[ly][lx]) {
          const worldX = state.pieceX + lx;
          const worldY = state.pieceY + ly;
          if (worldY >= 0) {
            drawCell(ctx, worldX, worldY, color, 1);
          }
        }
      }
    }
  }

  ctx.restore();
}

function renderEffectBadges(
  ctx: CanvasRenderingContext2D,
  state: PlayerNetState,
): void {
  const labels: string[] = [];
  if (state.speedBoostTicks > 0) labels.push("SPEED x2");
  if (state.flipScreenTicks > 0) labels.push("FLIPPED");
  if (labels.length === 0) return;

  ctx.save();
  ctx.font = "12px monospace";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#fff";

  let y = 4;
  for (const label of labels) {
    ctx.fillText(label, 6, y);
    y += 14;
  }
  ctx.restore();
}

function renderBoard(state: GameState): void {
  const ctx = mainCtx;

  // Clear
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

  // Draw grid lines
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 1;
  for (let x = 0; x <= BOARD_WIDTH; x++) {
    ctx.beginPath();
    ctx.moveTo(x * CELL_SIZE, 0);
    ctx.lineTo(x * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);
    ctx.stroke();
  }
  for (let y = 0; y <= BOARD_HEIGHT; y++) {
    ctx.beginPath();
    ctx.moveTo(0, y * CELL_SIZE);
    ctx.lineTo(BOARD_WIDTH * CELL_SIZE, y * CELL_SIZE);
    ctx.stroke();
  }

  // Draw locked cells
  for (let y = 0; y < BOARD_HEIGHT; y++) {
    for (let x = 0; x < BOARD_WIDTH; x++) {
      const cell = state.board[y][x];
      if (cell !== 0) {
        drawCell(ctx, x, y, COLORS[cell], 1);
      }
    }
  }

  // Draw ghost piece
  if (state.currentPiece) {
    const ghostY = getGhostY(state.board, state.currentPiece);
    if (ghostY !== state.currentPiece.y) {
      const ghostPiece = { ...state.currentPiece, y: ghostY };
      const cells = getCells(ghostPiece);
      const color = COLORS[state.currentPiece.type];
      for (const [x, y] of cells) {
        if (y >= 0) {
          drawCell(ctx, x, y, color, GHOST_ALPHA);
        }
      }
    }

    // Draw current piece
    const cells = getCells(state.currentPiece);
    const color = COLORS[state.currentPiece.type];
    for (const [x, y] of cells) {
      if (y >= 0) {
        drawCell(ctx, x, y, color, 1);
      }
    }
  }

  // Game over overlay
  if (state.gameOver) {
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

    ctx.fillStyle = "#fff";
    ctx.font = "bold 24px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      "GAME OVER",
      (BOARD_WIDTH * CELL_SIZE) / 2,
      (BOARD_HEIGHT * CELL_SIZE) / 2 - 20,
    );
    ctx.font = "16px monospace";
    ctx.fillText(
      "Press R to restart",
      (BOARD_WIDTH * CELL_SIZE) / 2,
      (BOARD_HEIGHT * CELL_SIZE) / 2 + 20,
    );
  }
}

function drawCell(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  alpha: number,
): void {
  const padding = 2;
  ctx.globalAlpha = alpha;

  // Main color
  ctx.fillStyle = color;
  ctx.fillRect(
    x * CELL_SIZE + padding,
    y * CELL_SIZE + padding,
    CELL_SIZE - padding * 2,
    CELL_SIZE - padding * 2,
  );

  // Highlight (top-left)
  ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
  ctx.fillRect(
    x * CELL_SIZE + padding,
    y * CELL_SIZE + padding,
    CELL_SIZE - padding * 2,
    3,
  );
  ctx.fillRect(
    x * CELL_SIZE + padding,
    y * CELL_SIZE + padding,
    3,
    CELL_SIZE - padding * 2,
  );

  // Shadow (bottom-right)
  ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
  ctx.fillRect(
    x * CELL_SIZE + padding,
    y * CELL_SIZE + CELL_SIZE - padding - 3,
    CELL_SIZE - padding * 2,
    3,
  );
  ctx.fillRect(
    x * CELL_SIZE + CELL_SIZE - padding - 3,
    y * CELL_SIZE + padding,
    3,
    CELL_SIZE - padding * 2,
  );

  ctx.globalAlpha = 1;
}

function renderNext(pieceType: PieceType): void {
  const ctx = nextCtx;
  const size = 20;

  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(0, 0, 80, 80);

  const shape = getShape(pieceType, 0);
  const color = COLORS[pieceType];

  // Center the piece
  const offsetX = pieceType === 1 ? 0 : pieceType === 2 ? 10 : 10;
  const offsetY = pieceType === 1 ? 20 : pieceType === 2 ? 20 : 15;

  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x]) {
        ctx.fillStyle = color;
        ctx.fillRect(
          offsetX + x * size + 1,
          offsetY + y * size + 1,
          size - 2,
          size - 2,
        );
      }
    }
  }
}

function renderHold(pieceType: PieceType | null): void {
  const ctx = holdCtx;
  const size = 20;

  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(0, 0, 80, 80);

  if (!pieceType) return;

  const shape = getShape(pieceType, 0);
  const color = COLORS[pieceType];

  const offsetX = pieceType === 1 ? 0 : pieceType === 2 ? 10 : 10;
  const offsetY = pieceType === 1 ? 20 : pieceType === 2 ? 20 : 15;

  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x]) {
        ctx.fillStyle = color;
        ctx.fillRect(
          offsetX + x * size + 1,
          offsetY + y * size + 1,
          size - 2,
          size - 2,
        );
      }
    }
  }
}

function renderStats(state: GameState): void {
  setTextContent(scoreEl, state.score.toString());
  setTextContent(linesEl, state.lines.toString());
  setTextContent(levelEl, state.level.toString());
  renderInventory([]);
}

const POWERUP_NAMES: Record<number, string> = {
  1: 'S', // Send Line
  2: 'F', // Flip Row
  3: 'V', // Flip Screen (Visual)
  4: 'X', // Speed Up
  5: 'R', // Randomize
  6: 'N', // Rain
};

const POWERUP_COLORS: Record<number, string> = {
  1: '#d44',
  2: '#4a4',
  3: '#44d',
  4: '#da4',
  5: '#4ad',
  6: '#58a',
};

export function renderInventory(inventory: number[], count: number = 0): void {
  const container = document.getElementById('inventory');
  if (!container) return;

  const slots = container.querySelectorAll('.inv-slot');
  slots.forEach((slot, i) => {
    const el = slot as HTMLElement;
    el.className = 'inv-slot';

    if (i < count && inventory[i] > 0) {
      const type = inventory[i];
      el.textContent = POWERUP_NAMES[type] || '?';
      el.classList.add('filled', `slot-${type}`);
    } else {
      el.textContent = (i + 1).toString();
    }
  });
}
